/**
 * Delivery step of the cart flow.
 *
 * The service (DPD / AIT / Home Install) is decided server-side from the basket's
 * heaviest item - see sections/cart-flow-delivery.liquid. This file only builds the
 * date picker for the two services that offer one, and saves the choice to the cart.
 *
 * Why the dates are built here rather than in Liquid: the working-day walk has to skip
 * weekends and bank holidays, and doing that with Liquid date arithmetic is both awkward
 * and untestable. "Now" still comes from the server, so the cut-off is judged in the
 * shop's timezone rather than the shopper's.
 */

/**
 * @typedef {object} WorkingDayInput
 * @property {string} now - Shop-local time, 'YYYY-MM-DDTHH:MM'
 * @property {number} minDays - earliest selectable working day (1 = next working day)
 * @property {number} maxDays - latest selectable working day
 * @property {number} cutoffHour - orders at or after this hour count from the next day
 * @property {string[]} holidays - non-working dates as 'YYYY-MM-DD'
 */

/**
 * The selectable delivery dates, as 'YYYY-MM-DD'.
 * @param {WorkingDayInput} input
 * @returns {string[]}
 */
export function workingDayOptions({ now, minDays, maxDays, cutoffHour, holidays = [] }) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(now ?? '');
  if (!match || !(maxDays >= minDays) || minDays < 1) return [];

  const [, y, m, d, hh] = match;
  const skip = new Set(holidays);

  // Anchored at midday UTC purely so no daylight-saving shift can move a date by a day.
  const cursor = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), 12));

  // Past the cut-off the order is treated as placed the following day, so counting starts
  // a day later. Counting always starts from the day AFTER the effective order date.
  let offset = Number(hh) >= cutoffHour ? 2 : 1;
  cursor.setUTCDate(cursor.getUTCDate() + offset);

  const dates = [];
  let workingDays = 0;

  // Bounded rather than while(true): 60 calendar days comfortably covers 14 working days
  // even against a run of holidays, and cannot hang the page if the inputs are odd.
  for (let i = 0; i < 60 && workingDays < maxDays; i++, cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const day = cursor.getUTCDay();
    if (day === 0 || day === 6) continue;

    const iso = cursor.toISOString().slice(0, 10);
    if (skip.has(iso)) continue;

    workingDays++;
    if (workingDays >= minDays) dates.push(iso);
  }

  return dates;
}

/**
 * @param {string} iso
 * @returns {string}
 */
function formatDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

function init() {
  const form = document.getElementById('cart-flow-delivery-form');
  if (!form) return;

  const select = /** @type {HTMLSelectElement | null} */ (form.querySelector('[data-delivery-date]'));

  if (select) {
    const holidays = (form.dataset.holidays || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const dates = workingDayOptions({
      now: form.dataset.now || '',
      minDays: Number(form.dataset.minDays),
      maxDays: Number(form.dataset.maxDays),
      cutoffHour: Number(form.dataset.cutoffHour),
      holidays,
    });

    select.replaceChildren();

    if (!dates.length) {
      // Never leave the placeholder in place claiming dates are loading.
      const option = new Option('No dates available - we will contact you', '');
      select.append(option);
      select.disabled = true;
    } else {
      select.append(new Option('Select a date', ''));
      for (const iso of dates) select.append(new Option(formatDate(iso), iso));

      // Restore a previous choice, but only if it is still one of the offered dates.
      const saved = form.dataset.savedDate;
      if (saved && dates.includes(saved)) select.value = saved;
    }
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('.cart-flow__continue'));
    const error = form.querySelector('[data-delivery-error]');

    if (select && !select.disabled && !select.value) {
      if (error instanceof HTMLElement) {
        error.textContent = 'Please choose a delivery date.';
        error.hidden = false;
      }
      select.focus();
      return;
    }

    if (error instanceof HTMLElement) error.hidden = true;
    if (button) {
      button.classList.add('is-loading');
      button.disabled = true;
    }

    const attributes = { delivery_service: form.dataset.service || '' };
    if (select && select.value) attributes.delivery_date = select.value;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error('cart update failed');
      window.location.href = '/checkout';
    } catch {
      if (button) {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
      if (error instanceof HTMLElement) {
        error.textContent = 'Could not save your delivery choice. Please try again.';
        error.hidden = false;
      }
    }
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
